import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group"
import { cn } from "../lib/utils"
import { ElementRenderer, liftV4Props } from "./element-renderer"

export function ToggleGroupRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  const atomDefaults = useAtomDefaults("toggle-group")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const selectionMode = (element as unknown as { selectionMode?: string }).selectionMode === "multiple"
    ? "multiple"
    : "single"
  const [boundSelection, setBoundSelection] = useBoundValue<string | string[]>(
    element.bind,
    resolveSymbol,
    selectionMode === "multiple" ? [] : "",
    (raw) => selectionMode === "multiple"
      ? (Array.isArray(raw) ? raw.map(String) : raw == null || raw === "" ? [] : [String(raw)])
      : (Array.isArray(raw) ? String(raw[raw.length - 1] ?? "") : String(raw ?? "")),
  )
  const selectedValues = Array.isArray(boundSelection)
    ? boundSelection
    : boundSelection === ""
      ? []
      : [boundSelection]

  const items = element.children ?? []

  return (
    <ToggleGroup
      value={selectedValues}
      onValueChange={(newValue: unknown) => {
        // base-ui ToggleGroup returns an array of pressed values
        const arr = newValue as string[]
        const normalized = selectionMode === "multiple"
          ? arr
          : arr.length > 0
            ? [arr[arr.length - 1]]
            : []
        const val = normalized[normalized.length - 1] ?? ""
        setBoundSelection(selectionMode === "multiple" ? normalized : val)
        // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
        dispatchEvent(element.id, "change", {
          ...(itemContext ?? {}),
          value: selectionMode === "multiple" ? normalized : val,
          values: normalized,
          selectedValues: normalized,
        })
      }}
      className={cn(element.className)}
      data-testid={`toggle-group-${element.id}`}
      data-atom-type="toggle-group"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-selection-mode={selectionMode}
      data-value={selectionMode === "multiple"
        ? JSON.stringify(selectedValues)
        : String(boundSelection)}
      data-values={JSON.stringify(selectedValues)}
      aria-label={element.ariaLabel ?? "Toggle group"}
    >
      {items.map((rawItem) => {
        // liftV4Props mirrors normalizeElement's first pass so v4-schema
        // items (`{ props: { text } }`) surface `item.text` here, the same
        // way card/slot renderers already do for their children. Without
        // this, a wrapped label is invisible to this renderer and the
        // display fallback below must not paper over that with the id —
        // an id is not a label.
        const item = liftV4Props(rawItem)
        const label = item.text ? String(resolveSymbol(item.text) ?? "") : ""
        const itemValue = String(
          resolveSymbol(String(item.value ?? item.text ?? item.id))
            ?? item.value
            ?? item.text
            ?? item.id,
        )

        return (
          <ToggleGroupItem
            key={item.id}
            value={itemValue}
            className={item.className}
            aria-label={item.ariaLabel ?? label}
            data-cogo-element-id={item.id}
            data-cogo-type={item.type}
            variant={(item.variant ?? "default") as "default" | "outline"}
            size={(item.size ?? "default") as "default" | "sm" | "lg"}
            onClick={() => {
              dispatchEvent(item.id, "tap", {
                ...(itemContext ?? {}),
                value: itemValue,
                selected: true,
              })
            }}
          >
            {label}
            {item.children?.map((child) => (
              <ElementRenderer key={child.id} element={child} />
            ))}
          </ToggleGroupItem>
        )
      })}
    </ToggleGroup>
  )
}
